<!-- eslint-disable vue/html-self-closing -->
<template>
  <div
    :class="[
      $style['c-big-select-input-search'],
      testClass('big-select-input-search'),
      $style[`theme-${theme}`],
    ]">
    <l-line-v3 align-x="between" class="u-flex-wrap  u-flex-nowrap@xs">
      <music-text-field-v3 :class="$style['search-text-field']">
        <music-icon-search-v3></music-icon-search-v3>
        <label :for="inputId">{{ label }}</label>
        <input
          :id="inputId"
          ref="inputSearchInput"
          v-model="headerLabel"
          type="text"
          autocomplete="off"
          :class="[
            $style['c-big-select-input-search__input'],
            testClass('big-select-input-search-input'),
            testClass(`input-${testSelector}`),
          ]"
          :disabled="disabled"
          :aria-controls="ddStore.listboxId"
          :name="name"
          @keydown.up.prevent="$emit('inputSearchUpArrow')"
          @keydown.down.prevent="$emit('inputSearchDownArrow')"
          @click.stop="$emit('inputSearchClick');"
          @focus="$emit('inputSearchFocus')"
          @keydown.enter.stop.prevent="handleInput($event.target.value)">
      </music-text-field-v3>
      <button
        type="button"
        class="c-button-v3  c-button-v3--tertiary"
        @click.prevent="handleInput(headerLabel)">
        Search
      </button>
    </l-line-v3>
    <div
      :class="$style['ua-school-search-feedback']"
      aria-live="assertive"
      role="status">
      <p
        v-if="fuzzySearchTerms"
        :key="searchKey"
        :class="[$style['ua-school-search-feedback__fuzzy-search-message'], testClass('fuzzy-search-msg')]"
      >
        Unrecognized words. Switched to "{{ fuzzySearchTerms }}".
      </p>

      <!-- :key is used to force re-render of messageResults,
      prompting screen readers to re-read unchanged
      results upon clicking Search button -->
      <p
        v-if="resultMessages?.message"
        :key="searchKey"
        :class="[$style['ua-school-search-feedback__message'], testClass('result-msg')]"
        v-html="resultMessages?.message" />
    </div>
    <div
      :class="[
        $style['c-big-select-input-search__body'],
        testClass('c-big-select-input-search-body')]">
      <slot />
    </div>
  </div>
</template>

<script setup>
  import { computed, inject, nextTick, ref, watch } from 'vue';
  import { testClass, validateEnum } from 'music';

  /** @typedef {import('../../types.js').ResultMessages} ResultMessages */
  const props = defineProps({
    inputId: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    headerText: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    searchKey: {
      type: Number,
      default: 0,
    },
    /** @type {ResultMessages{}} */
    resultMessages: {
      type: Object,
      default: null,
    },
    testSelector: {
      type: String,
      default: '',
    },
    theme: {
      type: String,
      default: 'm4',
      validate: (val) => validateEnum(val, 'theme'),
    },
  });

  const emit = defineEmits([
    'inputSearchClick',
    'inputSearchFocus',
    'inputSearchUpArrow',
    'inputSearchDownArrow',
    'inputSearchChange',
  ]);

  const ddStore = inject('dropdownStore');
  const inputSearchInput = ref(null);

  watch(
    () => ddStore.value.focusedIndex,
    async (newIndex) => {
      if (newIndex === -1) {
        await nextTick();
        inputSearchInput.value.focus();
      }
    }
  );

  watch(
    () => props.headerText,
    async (newText, oldText) => {
      if (newText != oldText) {
        headerLabel.value = newText;
      }
    }
  );

  const headerLabel = ref(props.headerText);

  const empty = computed(() => {
    return ddStore.value.selectedIndex < 0;
  });

  // Extract fuzzy search terms from  unrecognizedTerms in resultMessages
  const fuzzySearchTerms = computed(() => {
    if (!props.resultMessages?.unrecognizedTerms?.length) {
      return null;
    }

    const fuzzyTerms = props.resultMessages.unrecognizedTerms
      .map(term => {
        // Match pattern: "We used <b>term</b>"
        const match = term.match(/We used\s*<b>(.*?)<\/b>/i);
        return match ? match[1] : null;
      })
      .filter(Boolean);

    return fuzzyTerms.join(' ');
  });

  const handleInput = (text) => {
    const inputText = text;
    headerLabel.value = inputText;
    emit('inputSearchChange', inputText);
  };

</script>

<style lang="scss" module>
  @use 'MusicV3/v3/styles/base';
  @use '../../themes/input_search';

  .c-big-select-input-search {
    border-width: 0;
    display: block;
    line-height: 1.5;
    position: relative;

    music-icon-search-v3 svg {
      fill: var(--input-search-icon-fill-color);
    }

    .search-text-field {
      width: 100%;
    }

    &__input {
      font-weight: var(--music-font-semibold, 600);
      width: 100%;
    }

    &__body {
      padding: 0;
    }
  }

  .ua-school-search-feedback {
    align-self: flex-start;
    color: var(--music-true-gray-600, #666);
    text-align: left;

    &__message {
      margin-bottom: 1.5rem;
      margin-top: 0.5rem;
    }

    &__fuzzy-search-message {
      padding: 0.5rem 0;
    }
  }

</style>
